import type { PlayerState } from './player.js'

/**
 * Ukládání rozdělaného kurzu.
 *
 * V původní aplikaci znamenal refresh ztrátu celého kurzu — `/player`
 * nebyl v rewrites, takže vracel 404, a postup se nikde neukládal.
 * Uživatel, kterému zamrzlo video, přišel po F5 o všechno.
 *
 * Záměrně jen na straně prohlížeče: server ani kontrakt se nemění.
 */

export const PROGRESS_VERSION = 1

export interface SavedProgress {
  version: number
  lecture: string
  userId: string
  savedAt: number
  queue: readonly number[]
  cursor: number
  attempts: Readonly<Record<number, number>>
  totalPoints: number
  totalMaxPoints: number
  answers: PlayerState['scoring']['answers']
}

/** Starší záznamy zahodíme — rozdělaný kurz nemá smysl držet týdny. */
export const MAX_AGE_MS = 24 * 60 * 60 * 1000

export function storageKey(lecture: string, userId: string): string {
  return `safetutor:progress:${userId}:${lecture}`
}

export function toSaved(
  state: PlayerState,
  lecture: string,
  userId: string,
  savedAt: number,
): SavedProgress {
  return {
    version: PROGRESS_VERSION,
    lecture,
    userId,
    savedAt,
    queue: [...state.queue],
    cursor: state.cursor,
    attempts: { ...state.attempts },
    totalPoints: state.scoring.totalPoints,
    totalMaxPoints: state.scoring.totalMaxPoints,
    answers: [...state.scoring.answers],
  }
}

/** Ověří tvar i stáří. Cokoli podezřelého se zahodí, nikdy nespadne. */
export function parseSaved(
  raw: string | null,
  lecture: string,
  userId: string,
  now: number,
): SavedProgress | null {
  if (!raw) return null

  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }

  if (!data || typeof data !== 'object') return null
  const p = data as Partial<SavedProgress>

  if (p.version !== PROGRESS_VERSION) return null
  if (p.lecture !== lecture || p.userId !== userId) return null
  if (typeof p.savedAt !== 'number' || now - p.savedAt > MAX_AGE_MS) return null
  if (!Array.isArray(p.queue) || typeof p.cursor !== 'number') return null
  if (typeof p.totalPoints !== 'number' || typeof p.totalMaxPoints !== 'number') return null
  if (!Array.isArray(p.answers) || !p.attempts || typeof p.attempts !== 'object') return null
  if (p.cursor < 0 || p.cursor > p.queue.length) return null

  return p as SavedProgress
}

/* ------------------------------------------------------------------ *
 * Uložiště — v testech i v prohlížeči bez localStorage nespadne
 * ------------------------------------------------------------------ */

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Safari v soukromém režimu umí na přístup vyhodit výjimku
    return null
  }
}

export function saveProgress(
  state: PlayerState,
  lecture: string,
  userId: string,
  now: number,
): void {
  const store = storage()
  if (!store) return
  try {
    store.setItem(storageKey(lecture, userId), JSON.stringify(toSaved(state, lecture, userId, now)))
  } catch {
    // Plná kvóta nesmí shodit kurz
  }
}

export function loadProgress(lecture: string, userId: string, now: number): SavedProgress | null {
  const store = storage()
  if (!store) return null
  try {
    return parseSaved(store.getItem(storageKey(lecture, userId)), lecture, userId, now)
  } catch {
    return null
  }
}

export function clearProgress(lecture: string, userId: string): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(storageKey(lecture, userId))
  } catch {
    /* nevadí */
  }
}
